import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Star, Headphones, UserCheck, Sparkles, Shield, Clock, CreditCard, ChevronLeft, ChevronRight, Plane, Hotel, Map, ArrowRight, Play, Globe, Car, Ticket, Users } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_VIDEO = `${API}/static/hero-nature.mp4`;
const OCEAN_VIDEO = `${API}/static/ocean-video.mp4`;

/* ───── Hero Section ───── */
function HeroSection() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) navigate(`/explore?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <section data-testid="hero-section" className="relative h-screen min-h-[600px] flex items-center justify-center overflow-hidden">
      {/* Video Background */}
      <div className="absolute inset-0 hero-video-container">
        <video autoPlay muted loop playsInline preload="auto" crossOrigin="anonymous"
          poster="https://images.unsplash.com/photo-1561501900-3701fa6a0864?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjByZXNvcnQlMjBwb29sJTIwc3Vuc2V0fGVufDB8fHx8MTc3NDYwMjYxOHww&ixlib=rb-4.1.0&q=85"
          className="w-full h-full object-cover">
          <source src={HERO_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/60" />
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        <p className="font-body text-sm tracking-[0.25em] uppercase text-gold mb-4 animate-fade-up opacity-0 stagger-1">
          Welcome to
        </p>
        <h1 data-testid="hero-heading" className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 animate-fade-up opacity-0 stagger-2">
          BEYOND <span className="text-gold-gradient">HORIZONS</span>
        </h1>
        <p className="font-body text-base sm:text-lg text-white/80 max-w-2xl mx-auto mb-3 animate-fade-up opacity-0 stagger-3">
          Where every journey begins with a dream and ends with unforgettable memories
        </p>
        <p className="font-body text-sm text-gold tracking-widest mb-10 animate-fade-up opacity-0 stagger-3">
          Flights &bull; Hotels &bull; Holiday Packages
        </p>

        {/* Search Bar */}
        <form onSubmit={handleSearch} data-testid="hero-search-bar" className="glass rounded-full flex items-center max-w-2xl mx-auto px-2 py-2 animate-fade-up opacity-0 stagger-4">
          <div className="flex-1 flex items-center px-4">
            <Search className="w-5 h-5 text-gray-400 shrink-0" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search destinations, packages, hotels..."
              data-testid="hero-search-input"
              className="w-full bg-transparent border-none outline-none px-3 py-2 font-body text-sm text-navy placeholder:text-gray-400"
            />
          </div>
          <button type="submit" data-testid="hero-search-submit" className="bg-gold hover:bg-gold-dark text-white rounded-full px-6 py-2.5 font-body text-sm font-medium transition-all shrink-0">
            Search
          </button>
        </form>

        {/* Trust Indicators */}
        <div className="flex flex-wrap justify-center gap-4 sm:gap-8 mt-8 animate-fade-up opacity-0 stagger-5">
          {[
            { icon: <Headphones className="w-4 h-4" />, text: '24x7 Support' },
            { icon: <UserCheck className="w-4 h-4" />, text: '100% Personalised' },
            { icon: <Star className="w-4 h-4" />, text: '4.9 Rated' },
          ].map((item) => (
            <div key={item.text} className="flex items-center gap-2 glass-dark rounded-full px-4 py-2">
              <span className="text-gold">{item.icon}</span>
              <span className="font-body text-xs text-white/90">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Destinations Grid ───── */
function DestinationsSection({ destinations }) {
  return (
    <section data-testid="destinations-section" className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Discover</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-navy mb-4">Popular Destinations</h2>
          <p className="font-body text-base text-gray-500 max-w-xl mx-auto">Handpicked destinations that promise extraordinary experiences</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[200px] md:auto-rows-[220px]">
          {destinations.slice(0, 6).map((dest, i) => {
            const spans = [
              'md:col-span-8 md:row-span-2',
              'md:col-span-4 md:row-span-1',
              'md:col-span-4 md:row-span-1',
              'md:col-span-4 md:row-span-2',
              'md:col-span-4 md:row-span-1',
              'md:col-span-4 md:row-span-1',
            ];
            return (
              <Link to={`/explore?q=${dest.name}`} key={dest.id} data-testid={`dest-card-${dest.name.toLowerCase()}`}
                className={`dest-card ${spans[i]} group cursor-pointer`}>
                <img src={dest.image} alt={dest.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 z-[2] flex flex-col justify-end p-5">
                  <h3 className="font-heading text-xl sm:text-2xl font-bold text-white drop-shadow-lg">{dest.name}</h3>
                  <p className="font-body text-sm text-white/80 mt-1">From Rs.{dest.starting_price.toLocaleString('en-IN')}</p>
                </div>
                <div className="absolute top-4 right-4 z-[2] glass-dark rounded-full px-3 py-1">
                  <span className="font-body text-xs text-gold flex items-center gap-1"><Star className="w-3 h-3 fill-gold text-gold" />{dest.rating}</span>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="text-center mt-10">
          <Link to="/explore" data-testid="view-all-destinations" className="inline-flex items-center gap-2 bg-navy text-white rounded-full px-8 py-3 font-body font-medium hover:bg-navy-light transition-all hover:-translate-y-0.5">
            View All Destinations <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ───── Packages Section ───── */
function PackagesSection({ packages }) {
  const [activeTab, setActiveTab] = useState('budget');
  const { addToCart } = useCart();
  const filtered = packages.filter(p => p.category === activeTab);

  return (
    <section data-testid="packages-section" className="section-padding bg-navy-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Travel Packages</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-navy mb-4">Curated For You</h2>
          <p className="font-body text-base text-gray-500 max-w-xl mx-auto">Choose from our handcrafted budget-friendly and luxury travel packages</p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center gap-3 mb-10">
          {[{k:'budget',l:'Budget Packages',icon:<CreditCard className="w-4 h-4"/>},{k:'luxury',l:'Luxury Packages',icon:<Sparkles className="w-4 h-4"/>}].map(t=>(
            <button key={t.k} onClick={()=>setActiveTab(t.k)} data-testid={`tab-${t.k}`}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-body text-sm font-medium transition-all ${activeTab===t.k?'bg-gold text-white shadow-lg shadow-gold/25':'bg-white text-navy border border-gray-200 hover:border-gold/50'}`}>
              {t.icon}{t.l}
            </button>
          ))}
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.slice(0, 6).map((pkg) => (
            <div key={pkg.id} data-testid={`pkg-card-${pkg.id}`} className="pkg-card bg-white rounded-3xl overflow-hidden border border-gray-100">
              <div className="relative h-52">
                <img src={pkg.image} alt={pkg.name} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute top-3 left-3 bg-navy/80 backdrop-blur-sm text-white text-xs font-body px-3 py-1 rounded-full capitalize">{pkg.category}</div>
                <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-navy text-xs font-body px-3 py-1 rounded-full flex items-center gap-1">
                  <Star className="w-3 h-3 fill-gold text-gold" />{pkg.rating}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-heading text-lg font-semibold text-navy">{pkg.name}</h3>
                <p className="font-body text-xs text-gray-500 mt-1 flex items-center gap-1"><Map className="w-3 h-3" />{pkg.destination} &bull; {pkg.duration}</p>
                <p className="font-body text-sm text-gray-500 mt-2 line-clamp-2">{pkg.description}</p>
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                  <div>
                    <span className="font-body text-xs text-gray-400">Starting from</span>
                    <p className="font-heading text-xl font-bold text-gold">Rs.{pkg.price.toLocaleString('en-IN')}</p>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => addToCart(pkg.id)} data-testid={`add-cart-${pkg.id}`}
                      className="bg-gold text-white hover:bg-gold-dark rounded-full px-4 py-2 text-xs font-body font-medium transition-all">
                      Add to Cart
                    </button>
                    <Link to={`/packages/${pkg.id}`} data-testid={`view-pkg-${pkg.id}`}
                      className="bg-navy text-white hover:bg-navy-light rounded-full px-4 py-2 text-xs font-body font-medium transition-all">
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Flights & Hotels ───── */
function FlightsHotelsSection() {
  const services = [
    { icon: <Plane className="w-8 h-8" />, title: 'Flight Bookings', desc: 'Best deals on domestic and international flights with 24/7 booking support.' },
    { icon: <Hotel className="w-8 h-8" />, title: 'Hotel Stays', desc: 'Handpicked hotels from budget to 5-star luxury for every destination.' },
    { icon: <Map className="w-8 h-8" />, title: 'Custom Travel Plans', desc: 'Let our AI create personalized itineraries tailored to your preferences.' },
  ];
  return (
    <section data-testid="services-section" className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Our Services</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-navy mb-4">Flights & Hotels</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((s) => (
            <div key={s.title} data-testid={`service-${s.title.toLowerCase().replace(/\s/g,'-')}`}
              className="text-center p-8 rounded-3xl bg-navy-50 border border-gray-100 hover:-translate-y-2 transition-transform duration-300">
              <div className="w-16 h-16 bg-gold/10 rounded-2xl flex items-center justify-center mx-auto mb-5 text-gold">{s.icon}</div>
              <h3 className="font-heading text-xl font-semibold text-navy mb-3">{s.title}</h3>
              <p className="font-body text-sm text-gray-500">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Video Section ───── */
function VideoSection() {
  return (
    <section data-testid="video-section" className="relative h-[60vh] min-h-[400px] overflow-hidden">
      <video autoPlay muted loop playsInline preload="auto" crossOrigin="anonymous" className="absolute inset-0 w-full h-full object-cover">
        <source src={OCEAN_VIDEO} type="video/mp4" />
      </video>
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
        <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center mb-8 cursor-pointer hover:scale-110 transition-transform">
          <Play className="w-8 h-8 text-white fill-white ml-1" />
        </div>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">Explore Beyond Limits</h2>
        <p className="font-body text-lg text-white/80">Discover the world with Beyond Horizons</p>
      </div>
    </section>
  );
}

/* ───── Visa Services Section ───── */
function VisaServicesSection() {
  const countries = [
    { name: 'USA', flag: '🇺🇸', time: '15-30 days' },
    { name: 'UK', flag: '🇬🇧', time: '15-20 days' },
    { name: 'UAE', flag: '🇦🇪', time: '3-5 days' },
    { name: 'Thailand', flag: '🇹🇭', time: '5-7 days' },
    { name: 'Europe', flag: '🇪🇺', time: '10-15 days' },
    { name: 'Australia', flag: '🇦🇺', time: '20-30 days' },
  ];
  return (
    <section data-testid="visa-services-section" className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Visa Assistance</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-navy mb-4">Visa Services for 120+ Countries</h2>
          <p className="font-body text-base text-gray-500 max-w-xl mx-auto">Fast, reliable, and hassle-free visa assistance worldwide</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
          {countries.map(c => (
            <div key={c.name} className="text-center p-5 rounded-2xl bg-navy-50 border border-gray-100 hover:-translate-y-1 transition-transform duration-300">
              <span className="text-4xl mb-2 block">{c.flag}</span>
              <h4 className="font-heading text-sm font-semibold text-navy">{c.name}</h4>
              <p className="font-body text-xs text-gray-400 mt-1">{c.time}</p>
            </div>
          ))}
        </div>
        <div className="text-center">
          <Link to="/visa" data-testid="visa-apply-now" className="inline-flex items-center gap-2 bg-gold text-white hover:bg-gold-dark rounded-full px-8 py-3 font-body font-medium transition-all hover:-translate-y-0.5 hover:shadow-[0_4px_14px_0_rgba(197,160,89,0.39)]">
            <Globe className="w-4 h-4" /> Apply Now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ───── Car Rental Section ───── */
function CarRentalSection() {
  const options = [
    { icon: <Car className="w-6 h-6" />, title: 'Self-Drive Cars', desc: 'Freedom to explore at your own pace' },
    { icon: <Users className="w-6 h-6" />, title: 'Chauffeur-Driven', desc: 'Sit back and enjoy the ride' },
    { icon: <Sparkles className="w-6 h-6" />, title: 'Luxury Cars', desc: 'Travel in style and comfort' },
  ];
  return (
    <section data-testid="car-rental-section" className="section-padding bg-navy-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Travel in Style</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-navy mb-4">Car Rentals Worldwide</h2>
            <p className="font-body text-base text-gray-500 mb-8">From self-drive adventures to chauffeur-driven luxury — we have the perfect ride for every journey.</p>
            <div className="space-y-4 mb-8">
              {options.map(o => (
                <div key={o.title} className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-gray-100">
                  <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center text-gold shrink-0">{o.icon}</div>
                  <div>
                    <h4 className="font-heading text-base font-semibold text-navy">{o.title}</h4>
                    <p className="font-body text-xs text-gray-500">{o.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/car-rental" data-testid="car-book-ride" className="inline-flex items-center gap-2 bg-gold text-white hover:bg-gold-dark rounded-full px-8 py-3 font-body font-medium transition-all hover:-translate-y-0.5">
              <Car className="w-4 h-4" /> Book Your Ride <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="rounded-3xl overflow-hidden h-[400px]">
            <img src="https://images.pexels.com/photos/164634/pexels-photo-164634.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Car Rental" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── Tickets Section ───── */
function TicketsSection() {
  const activities = [
    { title: 'Theme Parks', desc: 'Disneyland, Universal Studios, Ferrari World', icon: '🎢' },
    { title: 'City Tours', desc: 'Guided tours of the world\'s most iconic cities', icon: '🏛️' },
    { title: 'Adventure', desc: 'Skydiving, scuba diving, bungee jumping', icon: '🏔️' },
    { title: 'Events', desc: 'Shows, concerts, cultural performances', icon: '🎭' },
  ];
  return (
    <section data-testid="tickets-section" className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Experiences</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-navy mb-4">Book Tickets & Experiences</h2>
          <p className="font-body text-base text-gray-500 max-w-xl mx-auto">Theme parks, city tours, adventure activities, and events worldwide</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
          {activities.map(a => (
            <div key={a.title} className="text-center p-6 rounded-3xl bg-navy-50 border border-gray-100 hover:-translate-y-2 transition-transform duration-300">
              <span className="text-4xl mb-3 block">{a.icon}</span>
              <h4 className="font-heading text-base font-semibold text-navy mb-1">{a.title}</h4>
              <p className="font-body text-xs text-gray-500">{a.desc}</p>
            </div>
          ))}
        </div>
        <div className="text-center">
          <Link to="/tickets" data-testid="tickets-explore" className="inline-flex items-center gap-2 bg-navy text-white hover:bg-navy-light rounded-full px-8 py-3 font-body font-medium transition-all hover:-translate-y-0.5">
            <Ticket className="w-4 h-4" /> Explore Activities <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ───── Airport Transfer Section ───── */
function AirportTransferSection() {
  return (
    <section data-testid="airport-transfer-section" className="section-padding bg-navy">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden h-[350px] order-2 lg:order-1">
            <img src="https://images.pexels.com/photos/4530180/pexels-photo-4530180.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Airport Transfer" className="w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="order-1 lg:order-2">
            <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Seamless Travel</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">Airport Transfers Made Easy</h2>
            <p className="font-body text-base text-gray-400 mb-8">Reliable pickup and drop services at 100+ airports worldwide. Professional drivers, flight tracking, and stress-free transfers.</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              {[
                { title: 'Pickup & Drop', desc: 'Door-to-door service' },
                { title: 'Private Transfer', desc: 'Exclusive vehicles' },
                { title: 'Luxury Transfer', desc: 'Premium experience' },
              ].map(t => (
                <div key={t.title} className="p-4 rounded-2xl bg-white/5 border border-white/10 text-center">
                  <h4 className="font-heading text-sm font-semibold text-white mb-1">{t.title}</h4>
                  <p className="font-body text-xs text-gray-400">{t.desc}</p>
                </div>
              ))}
            </div>
            <Link to="/airport-transfer" data-testid="transfer-book" className="inline-flex items-center gap-2 bg-gold text-white hover:bg-gold-dark rounded-full px-8 py-3 font-body font-medium transition-all hover:-translate-y-0.5">
              <Plane className="w-4 h-4" /> Book Transfer <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── Why Choose Us ───── */
function WhyChooseSection() {
  const reasons = [
    { icon: <CreditCard className="w-6 h-6" />, title: 'Best Price Guarantee', desc: 'We match any competitor price for the same experience.' },
    { icon: <Sparkles className="w-6 h-6" />, title: 'Customised Trips', desc: 'Every itinerary is crafted uniquely for you.' },
    { icon: <Shield className="w-6 h-6" />, title: 'Trusted Service', desc: 'Thousands of happy travelers trust us.' },
    { icon: <Clock className="w-6 h-6" />, title: 'Easy Booking', desc: 'Book in minutes with our seamless process.' },
    { icon: <Headphones className="w-6 h-6" />, title: '24/7 Support', desc: 'Round-the-clock assistance wherever you are.' },
  ];
  return (
    <section data-testid="why-choose-section" className="section-padding bg-navy">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Why Beyond Horizons</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">Why Choose Us</h2>
          <p className="font-body text-base text-gray-400 max-w-xl mx-auto">We go above and beyond to make your travel dreams come true</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          {reasons.map((r) => (
            <div key={r.title} className="text-center p-6 rounded-3xl bg-white/5 border border-white/10 hover:-translate-y-2 transition-transform duration-300">
              <div className="w-14 h-14 bg-gold/20 rounded-2xl flex items-center justify-center mx-auto mb-4 text-gold">{r.icon}</div>
              <h4 className="font-heading text-base font-semibold text-white mb-2">{r.title}</h4>
              <p className="font-body text-xs text-gray-400">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── Testimonials ───── */
function TestimonialsSection({ testimonials }) {
  const [active, setActive] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (testimonials.length === 0) return;
    timerRef.current = setInterval(() => setActive(p => (p + 1) % testimonials.length), 5000);
    return () => clearInterval(timerRef.current);
  }, [testimonials.length]);

  if (testimonials.length === 0) return null;

  const goTo = (dir) => {
    clearInterval(timerRef.current);
    setActive(p => dir === 'next' ? (p + 1) % testimonials.length : (p - 1 + testimonials.length) % testimonials.length);
  };

  const t = testimonials[active];

  return (
    <section data-testid="testimonials-section" className="section-padding bg-white">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Testimonials</p>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-navy mb-12">What Travelers Say</h2>

        <div className="relative">
          <div className="flex items-center justify-center gap-1 mb-6">
            {Array.from({ length: t.rating }).map((_, i) => (
              <Star key={i} className="w-5 h-5 fill-gold text-gold" />
            ))}
          </div>
          <p className="font-body text-lg text-gray-600 italic leading-relaxed max-w-2xl mx-auto mb-8">"{t.text}"</p>
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center font-heading text-lg font-bold text-gold">{t.avatar}</div>
            <div className="text-left">
              <p className="font-body text-sm font-semibold text-navy">{t.name}</p>
              <p className="font-body text-xs text-gray-400">{t.location}</p>
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 mt-8">
            <button onClick={() => goTo('prev')} data-testid="testimonial-prev" className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-navy hover:text-white hover:border-navy transition-all">
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="flex gap-2">
              {testimonials.map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === active ? 'bg-gold w-6' : 'bg-gray-200'}`} />
              ))}
            </div>
            <button onClick={() => goTo('next')} data-testid="testimonial-next" className="w-10 h-10 rounded-full border border-gray-200 flex items-center justify-center hover:bg-navy hover:text-white hover:border-navy transition-all">
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ───── CTA Section ───── */
function CTASection() {
  return (
    <section data-testid="cta-section" className="relative py-24 overflow-hidden bg-navy">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-72 h-72 bg-gold rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold rounded-full blur-3xl translate-x-1/3 translate-y-1/3" />
      </div>
      <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-5">Let Us Take You<br /><span className="text-gold-gradient">Beyond Horizons</span></h2>
        <p className="font-body text-lg text-gray-400 mb-10">Your dream vacation is just a click away. Let our experts craft the perfect journey for you.</p>
        <Link to="/contact" data-testid="cta-plan-trip" className="inline-flex items-center gap-2 bg-gold text-white hover:bg-gold-dark rounded-full px-10 py-4 font-body font-semibold text-lg transition-all hover:shadow-[0_4px_20px_0_rgba(197,160,89,0.4)] hover:-translate-y-1">
          Plan Your Trip <ArrowRight className="w-5 h-5" />
        </Link>
      </div>
    </section>
  );
}

/* ───── Blog Section ───── */
function BlogSection({ blogs }) {
  if (blogs.length === 0) return null;
  return (
    <section data-testid="blog-section" className="section-padding bg-navy-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">From Our Blog</p>
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-navy mb-4">Travel Stories & Tips</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {blogs.map((b) => (
            <div key={b.id} data-testid={`blog-card-${b.id}`} className="bg-white rounded-3xl overflow-hidden border border-gray-100 hover:-translate-y-2 transition-transform duration-300">
              <div className="h-48 overflow-hidden">
                <img src={b.image} alt={b.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" loading="lazy" />
              </div>
              <div className="p-5">
                <div className="flex items-center gap-3 mb-3">
                  <span className="font-body text-xs text-gold bg-gold/10 px-3 py-1 rounded-full">{b.category}</span>
                  <span className="font-body text-xs text-gray-400">{b.read_time}</span>
                </div>
                <h3 className="font-heading text-lg font-semibold text-navy mb-2 line-clamp-2">{b.title}</h3>
                <p className="font-body text-sm text-gray-500 line-clamp-2">{b.excerpt}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ───── HOMEPAGE ───── */
export default function HomePage() {
  const [destinations, setDestinations] = useState([]);
  const [packages, setPackages] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [blogs, setBlogs] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dRes, pRes, tRes, bRes] = await Promise.all([
          axios.get(`${API}/destinations`),
          axios.get(`${API}/packages`),
          axios.get(`${API}/testimonials`),
          axios.get(`${API}/blogs`),
        ]);
        setDestinations(dRes.data);
        setPackages(pRes.data);
        setTestimonials(tRes.data);
        setBlogs(bRes.data);
      } catch (e) { console.error('Failed to fetch data:', e); }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      <HeroSection />
      <DestinationsSection destinations={destinations} />
      <PackagesSection packages={packages} />
      <FlightsHotelsSection />
      <VisaServicesSection />
      <CarRentalSection />
      <TicketsSection />
      <AirportTransferSection />
      <VideoSection />
      <WhyChooseSection />
      <TestimonialsSection testimonials={testimonials} />
      <CTASection />
      <BlogSection blogs={blogs} />
    </div>
  );
}
